import classNames from "@/utils/classNames";
import { ReactNode, useState } from "react";
import { IoChevronDown } from "react-icons/io5";

export type FaqItem = {
    slug: string;
    title: string;
    icon?: ReactNode;
    body: ReactNode;
};

type FaqAccordionProps = {
    items: FaqItem[];
    panelIdPrefix: string;
    onItemOpened?: (slug: string) => void;
    variant?: "default" | "compact";
    className?: string;
};

export default function FaqAccordion({
    items,
    panelIdPrefix,
    onItemOpened,
    variant = "default",
    className = "",
}: FaqAccordionProps) {
    const [openSlug, setOpenSlug] = useState<string | null>(null);

    const isCompact = variant === "compact";

    const onToggle = (slug: string) => {
        const isOpening = openSlug !== slug;
        setOpenSlug(isOpening ? slug : null);
        if (isOpening && onItemOpened) {
            onItemOpened(slug);
        }
    };

    return (
        <ul
            className={classNames(
                "flex flex-col",
                isCompact ? "" : "border-y border-brand-border bg-brand-white",
                className,
            )}
        >
            {items.map((item, index) => {
                const isOpen = openSlug === item.slug;
                const panelId = `${panelIdPrefix}-${item.slug}`;
                return (
                    <li
                        key={item.slug}
                        className={
                            !isCompact && index > 0
                                ? "border-t border-brand-border"
                                : undefined
                        }
                    >
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() => onToggle(item.slug)}
                            className={classNames(
                                "flex w-full items-center text-left",
                                isCompact
                                    ? "gap-1 py-2"
                                    : "gap-3 px-6 py-3.5 transition-colors hover:bg-brand-lightGray/60",
                            )}
                        >
                            {item.icon && (
                                <span
                                    aria-hidden="true"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-lightRed text-base text-brand-red"
                                >
                                    {item.icon}
                                </span>
                            )}
                            <span
                                className={classNames(
                                    "grow text-sm text-brand-black",
                                    isCompact ? "" : "font-medium sm:text-base",
                                )}
                            >
                                {item.title}
                            </span>
                            <IoChevronDown
                                aria-hidden="true"
                                className={classNames(
                                    "shrink-0 transition-transform duration-200",
                                    isCompact
                                        ? "text-brand-black"
                                        : "text-brand-gray",
                                    isOpen ? "rotate-180" : "",
                                )}
                            />
                        </button>
                        <div
                            id={panelId}
                            role="region"
                            aria-hidden={!isOpen}
                            inert={!isOpen}
                            className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
                                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                            }`}
                        >
                            <div className="min-h-0 overflow-hidden">
                                <div
                                    className={classNames(
                                        "flex flex-col text-sm leading-relaxed text-brand-gray",
                                        isCompact
                                            ? "gap-2 pb-2"
                                            : "gap-3 px-6 pb-4",
                                    )}
                                >
                                    {item.body}
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
